package org.teddapp.project.ui.finance

import org.teddapp.project.ui.TeddSize

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Finance_account
import com.tedd.db.Finance_category
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTokens

/**
 * Panneau modal "Nouvelle transaction" — rendu en Dialog plein-écran avec un panneau
 * 440dp ancré à droite. Reproduit la slide-in du JSX.
 */
@Composable
internal fun AddTransactionPanel(
    account: Finance_account,
    accounts: List<Finance_account>,
    categories: List<Finance_category>,
    onClose: () -> Unit,
    onSave: (
        date: String,
        type: String,
        amount: Long,
        description: String?,
        catId: String?,
        toAccountId: String?,
        fundId: String?,
    ) -> Unit,
    existing: com.tedd.db.Finance_transaction? = null,
    onDelete: (() -> Unit)? = null,
    /** Crée une catégorie du type demandé et rend son identifiant. */
    onCreateCategory: (suspend (name: String, type: String) -> String?)? = null,
) {
    val isEdit = existing != null
    val scope = rememberCoroutineScope()
    var type by remember(existing?.id) { mutableStateOf(existing?.type ?: "expense") }
    var amount by remember(existing?.id) { mutableStateOf(existing?.amount?.toString() ?: "") }
    var description by remember(existing?.id) { mutableStateOf(existing?.description ?: "") }
    var date by remember(existing?.id) { mutableStateOf(existing?.date ?: TODAY_ISO) }
    val expenseCats = categories.filter { it.type == "expense" && it.is_deleted == 0L }
    val incomeCats = categories.filter { it.type == "income" && it.is_deleted == 0L }
    val cats = if (type == "income") incomeCats else expenseCats
    var catId by remember(existing?.id) {
        mutableStateOf(existing?.cat_id ?: expenseCats.firstOrNull()?.id)
    }
    var toAccountId by remember(existing?.id) {
        mutableStateOf(existing?.to_account_id ?: accounts.firstOrNull { it.id != account.id }?.id)
    }
    var fundId by remember(existing?.id) { mutableStateOf(existing?.fund_id) }
    val amountValue = amount.replace(",", ".").toDoubleOrNull() ?: 0.0
    val valid = amountValue > 0.0

    val fullScreen = org.teddapp.project.ui.LocalUseFullScreenForm.current
    Dialog(
        onDismissRequest = onClose,
        properties = DialogProperties(usePlatformDefaultWidth = false, dismissOnClickOutside = true),
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .then(
                    if (fullScreen) Modifier.background(TeddTokens.Bg)
                    else Modifier.background(Color.Black.copy(alpha = 0.35f))
                )
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onClose,
                ),
        ) {
            Column(
                modifier = Modifier
                    .then(
                        if (fullScreen) Modifier.fillMaxSize()
                        else Modifier.align(Alignment.CenterEnd).width(TeddSize.SidePanel).fillMaxHeight()
                    )
                    .background(TeddTokens.Bg)
                    .then(
                        if (fullScreen) Modifier
                        else Modifier.border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                    )
                    .then(if (fullScreen) Modifier.imePadding() else Modifier)
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = {},
                    ),
            ) {
                // Header
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                        .padding(horizontal = 22.dp, vertical = 16.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = if (isEdit) "MODIFIER LA TRANSACTION" else "NOUVELLE TRANSACTION",
                            color = TeddTokens.InkMuted,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.SemiBold,
                            letterSpacing = 0.6.sp,
                        )
                        Text(
                            text = account.name,
                            color = TeddTokens.Ink,
                            fontSize = 15.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                    }
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(TeddTokens.RXs))
                            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RXs))
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = onClose,
                            )
                            .padding(8.dp),
                        contentAlignment = Alignment.Center,
                    ) {
                        TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.InkMuted)
                    }
                }
                // Body
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .verticalScroll(rememberScrollState())
                        .padding(22.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                ) {
                    // Type segmented
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                            .padding(3.dp),
                    ) {
                        listOf("expense" to "Dépense", "income" to "Entrée", "transfer" to "Transfert").forEach { (k, label) ->
                            val on = type == k
                            Box(
                                modifier = Modifier
                                    .weight(1f)
                                    .height(30.dp)
                                    .clip(RoundedCornerShape(TeddTokens.RXs))
                                    .background(if (on) TeddTokens.Surface else Color.Transparent, RoundedCornerShape(TeddTokens.RXs))
                                    .clickable(
                                        interactionSource = remember { MutableInteractionSource() },
                                        indication = null,
                                        onClick = {
                                            type = k
                                            if (k == "income") catId = incomeCats.firstOrNull()?.id
                                            else if (k == "expense") catId = expenseCats.firstOrNull()?.id
                                        },
                                    ),
                                contentAlignment = Alignment.Center,
                            ) {
                                Text(
                                    text = label,
                                    color = if (on) TeddTokens.Ink else TeddTokens.InkMuted,
                                    fontSize = 12.5.sp,
                                    fontWeight = FontWeight.Medium,
                                )
                            }
                        }
                    }
                    // Amount
                    Column {
                        FieldLabel("Montant")
                        Spacer(modifier = Modifier.height(6.dp))
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(TeddTokens.RSm))
                                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                                .padding(horizontal = 14.dp, vertical = 10.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                        ) {
                            BasicTextField(
                                value = amount,
                                onValueChange = { v -> if (v.all { c -> c.isDigit() || c == '.' || c == ',' }) amount = v },
                                modifier = Modifier.weight(1f),
                                singleLine = true,
                                textStyle = TextStyle(
                                    color = TeddTokens.Ink,
                                    fontSize = 28.sp,
                                    fontWeight = FontWeight.Medium,
                                    letterSpacing = (-0.5).sp,
                                ),
                                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                                visualTransformation = ThousandsVisualTransformation(),
                                decorationBox = { inner ->
                                    if (amount.isEmpty()) Text("0", color = TeddTokens.InkMuted, fontSize = 28.sp)
                                    inner()
                                },
                            )
                            Text(
                                text = if (account.currency == "USD") "$" else "F",
                                color = TeddTokens.InkMuted,
                                fontSize = 14.sp,
                                fontWeight = FontWeight.Medium,
                            )
                        }
                    }
                    // Description
                    Column {
                        FieldLabel("Description")
                        Spacer(modifier = Modifier.height(6.dp))
                        TextField(value = description, onValueChange = { description = it }, placeholder = "Ex. Marché Sandaga")
                    }
                    // Date + Category/To
                    Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        Column(modifier = Modifier.weight(1f)) {
                            FieldLabel("Date")
                            Spacer(modifier = Modifier.height(6.dp))
                            TextField(value = date, onValueChange = { date = it }, placeholder = "YYYY-MM-DD")
                        }
                        Column(modifier = Modifier.weight(1f)) {
                            if (type != "transfer") {
                                FieldLabel("Catégorie")
                                Spacer(modifier = Modifier.height(6.dp))
                                SelectField(
                                    value = cats.firstOrNull { it.id == catId }?.name ?: "—",
                                    options = cats.map { it.id to it.name },
                                    onChoose = { catId = it },
                                )
                            } else {
                                FieldLabel("Vers")
                                Spacer(modifier = Modifier.height(6.dp))
                                val choices = accounts.filter { it.id != account.id && it.is_deleted == 0L }
                                SelectField(
                                    value = choices.firstOrNull { it.id == toAccountId }?.name ?: "—",
                                    options = choices.map { it.id to it.name },
                                    onChoose = { toAccountId = it },
                                )
                            }
                        }
                    }
                    // Quick pick
                    if (type != "transfer") {
                        Column {
                            FieldLabel("Choix rapide")
                            Spacer(modifier = Modifier.height(8.dp))
                            QuickPickRow(cats = cats, selectedId = catId, onSelect = { catId = it })
                            // Une dépense qui n'entre dans aucune catégorie finit dans
                            // « Loisirs » faute de mieux, et le classement ne veut plus
                            // rien dire. On crée la bonne ici, au moment où le besoin
                            // apparaît, plutôt que d'aller la chercher ailleurs.
                            if (onCreateCategory != null) {
                                Spacer(modifier = Modifier.height(10.dp))
                                NewCategoryInline(type = type) { nom ->
                                    scope.launch { catId = onCreateCategory.invoke(nom, type) ?: catId }
                                }
                            }
                        }
                    }
                }
                // Footer
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                        .padding(16.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    if (onDelete != null) {
                        TeddButton(
                            text = "Supprimer",
                            kind = TeddButtonKind.Danger,
                            size = TeddButtonSize.Md,
                            onClick = onDelete,
                            leadingIcon = { TeddIcon(name = "trash", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.dp) },
                        )
                    }
                    Spacer(modifier = Modifier.weight(1f))
                    TeddButton(text = "Annuler", kind = TeddButtonKind.Secondary, size = TeddButtonSize.Md, onClick = onClose)
                    TeddButton(
                        text = "Enregistrer",
                        kind = TeddButtonKind.Primary,
                        size = TeddButtonSize.Md,
                        enabled = valid,
                        onClick = {
                            val amt = amountValue.toLong()
                            onSave(
                                date,
                                type,
                                amt,
                                description.takeIf { it.isNotBlank() },
                                if (type == "transfer") null else catId,
                                if (type == "transfer") toAccountId else null,
                                if (type == "expense") fundId else null,
                            )
                        },
                        leadingIcon = { TeddIcon(name = "check", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.2.dp) },
                    )
                }
            }
        }
    }
}

@Composable
private fun FieldLabel(text: String) {
    Text(
        text = text,
        color = TeddTokens.InkMuted,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
    )
}

@Composable
private fun TextField(value: String, onValueChange: (String) -> Unit, placeholder: String) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(38.dp)
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp),
        contentAlignment = Alignment.CenterStart,
    ) {
        BasicTextField(
            value = value,
            onValueChange = onValueChange,
            singleLine = true,
            textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp),
            cursorBrush = SolidColor(TeddTokens.LimeStrong),
            decorationBox = { inner ->
                if (value.isEmpty()) Text(placeholder, color = TeddTokens.InkMuted, fontSize = 13.sp)
                inner()
            },
            modifier = Modifier.fillMaxWidth(),
        )
    }
}

@Composable
private fun SelectField(value: String, options: List<Pair<String, String>>, onChoose: (String) -> Unit) {
    var open by remember { mutableStateOf(false) }
    Box {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(38.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = { open = !open },
                )
                .padding(horizontal = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(text = value, color = TeddTokens.Ink, fontSize = 13.sp, modifier = Modifier.weight(1f), maxLines = 1)
            TeddIcon(name = "chevron", size = 12.dp, color = TeddTokens.InkMuted)
        }
        if (open) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .wrapContentHeight()
                    .padding(top = 40.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm)),
            ) {
                options.forEach { (id, label) ->
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = { onChoose(id); open = false },
                            )
                            .padding(horizontal = 12.dp, vertical = 8.dp),
                    ) {
                        Text(text = label, color = TeddTokens.Ink, fontSize = 12.5.sp)
                    }
                }
            }
        }
    }
}

@Composable
private fun QuickPickRow(cats: List<Finance_category>, selectedId: String?, onSelect: (String) -> Unit) {
    // Simple wrap by manual rows of 3
    val chunks = cats.chunked(3)
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        chunks.forEach { row ->
            Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                row.forEach { c ->
                    val on = c.id == selectedId
                    val color = parseFinHex(c.color)
                    Row(
                        modifier = Modifier
                            .clip(RoundedCornerShape(TeddTokens.RFull))
                            .background(if (on) color.copy(alpha = 0.10f) else TeddTokens.Surface, RoundedCornerShape(TeddTokens.RFull))
                            .border(1.dp, if (on) color else TeddTokens.Line, RoundedCornerShape(TeddTokens.RFull))
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = { onSelect(c.id) },
                            )
                            .padding(horizontal = 10.dp, vertical = 6.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(5.dp),
                    ) {
                        TeddIcon(name = c.icon, size = 11.dp, color = if (on) color else TeddTokens.InkMuted, strokeWidth = 2.dp)
                        Text(
                            text = c.name,
                            color = if (on) color else TeddTokens.Ink,
                            fontSize = 12.sp,
                            fontWeight = if (on) FontWeight.SemiBold else FontWeight.Medium,
                        )
                    }
                }
            }
        }
    }
}

/**
 * Créer une catégorie sans quitter la saisie.
 *
 * Le gestionnaire complet existe dans le tableau de bord, mais on n'y pense pas au
 * moment où l'on saisit une dépense : on choisit alors la catégorie la moins fausse,
 * et le classement se dégrade sans qu'on s'en aperçoive.
 */
@Composable
private fun NewCategoryInline(type: String, onCreate: (String) -> Unit) {
    var ouvert by remember { mutableStateOf(false) }
    var nom by remember { mutableStateOf("") }

    if (!ouvert) {
        TeddButton(
            text = if (type == "income") "+ Nouvelle catégorie d'entrée" else "+ Nouvelle catégorie de dépense",
            onClick = { ouvert = true },
            kind = TeddButtonKind.Ghost,
            size = TeddButtonSize.Sm,
        )
        return
    }

    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
        Box(modifier = Modifier.weight(1f)) {
            TextField(value = nom, onValueChange = { nom = it }, placeholder = "Nom de la catégorie")
        }
        TeddButton(
            text = "Créer",
            onClick = {
                val propre = nom.trim()
                if (propre.isNotEmpty()) {
                    onCreate(propre)
                    nom = ""
                    ouvert = false
                }
            },
            kind = TeddButtonKind.Secondary,
            size = TeddButtonSize.Sm,
            enabled = nom.isNotBlank(),
        )
        TeddButton(
            text = "Annuler",
            onClick = { nom = ""; ouvert = false },
            kind = TeddButtonKind.Ghost,
            size = TeddButtonSize.Sm,
        )
    }
}
